import { useState } from "react";
import type { ValidateForm } from "ra-core";
import { Form, required, useNotify, useTranslate } from "ra-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/admin/password-input";
import { Layout } from "@/components/supabase/layout";
import { supabase } from "../providers/supabase/supabase";

interface FormData {
  password: string;
  confirmPassword: string;
}

/**
 * Shown instead of the app when the signed-in user's sales row has
 * must_change_password=true (set for everyone still on the shared temp
 * password from the initial user seed). Unlike SetPasswordPage this runs
 * against an already-active session, not a magic-link recovery token.
 */
export const ForcePasswordChangePage = () => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const translate = useTranslate();

  const validate = (values: FormData) => {
    if (values.password !== values.confirmPassword) {
      return {
        password: "ra-supabase.validation.password_mismatch",
        confirmPassword: "ra-supabase.validation.password_mismatch",
      };
    }
    if (values.password.length < 8) {
      return { password: "Password must be at least 8 characters" };
    }
    return {};
  };

  const submit = async (values: FormData) => {
    setLoading(true);
    try {
      const { error: updateAuthError } = await supabase.auth.updateUser({
        password: values.password,
      });
      if (updateAuthError) throw updateAuthError;

      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user.id;
      if (userId) {
        await supabase
          .from("sales")
          .update({ must_change_password: false })
          .match({ user_id: userId });
      }

      window.localStorage.removeItem("RaStore.auth.current_sale");
      window.location.href = "/";
    } catch (error: any) {
      notify(error?.message ?? "ra.auth.sign_in_error", { type: "warning" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("ra-supabase.set_password.new_password", {
            _: "Choose a new password",
          })}
        </h1>
        <p className="text-sm text-muted-foreground">
          You're signed in with a temporary password. Set your own before continuing.
        </p>
      </div>
      <Form<FormData>
        className="space-y-8"
        onSubmit={submit as SubmitHandler<FieldValues>}
        validate={validate as ValidateForm}
      >
        <PasswordInput
          label={translate("ra.auth.password", { _: "Password" })}
          autoComplete="new-password"
          source="password"
          validate={required()}
        />
        <PasswordInput
          label={translate("ra.auth.confirm_password", { _: "Confirm password" })}
          source="confirmPassword"
          validate={required()}
        />
        <Button type="submit" className="cursor-pointer" disabled={loading}>
          {translate("ra.action.save")}
        </Button>
      </Form>
    </Layout>
  );
};

ForcePasswordChangePage.path = "force-password-change";
